import { useDownloadBill, useViewBill } from "@features/bx/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBulkBillDraftList } from "../hooks";

interface SingleBill {
  id: string;
  invoiceNumber: string;
  consumer: string;
  premise: string;
  billCycle: string;
  billingMonth: string;
  consumption: number;
  billedAmount: number;
  generatedDate: string;
  status: "delivered" | "paid" | "overdue" | "draft";
}

interface SingleBillFiltersType {
  status: string;
  billCycle: string;
  billingMonth: string;
  dateRange: {
    from: Date | null;
    to: Date | null;
  };
}

type SortField =
  | "consumer"
  | "billCycle"
  | "billingMonth"
  | "consumption"
  | "billedAmount"
  | "generatedDate";
type SortDirection = "asc" | "desc";

const getStatusBadgeConfig = (status: SingleBill["status"]) => {
  switch (status) {
    case "paid":
      return {
        variant: "default" as const,
        className: "bg-green-100 text-green-800",
      };
    case "delivered":
      return {
        variant: "secondary" as const,
        className: "bg-blue-100 text-blue-800",
      };
    case "overdue":
      return {
        variant: "destructive" as const,
        className: "bg-red-100 text-red-800",
      };
    case "draft":
      return {
        variant: "outline" as const,
        className: "bg-yellow-100 text-yellow-800",
      };
    default:
      return {
        variant: "outline" as const,
        className: "bg-gray-100 text-gray-800",
      };
  }
};

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const BulkBillDraft = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filters, setFilters] = useState<SingleBillFiltersType>({
    status: "all",
    billCycle: "all",
    billingMonth: "all",
    dateRange: {
      from: null,
      to: null,
    },
  });

  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();
  const CURRENCY = useCurrency();

  const { data: draftDraftBills } = useBulkBillDraftList(page);

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(field as SortField);
    setSortDirection(direction);
  };

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
  };

  const handleFiltersChange = (newFilters: SingleBillFiltersType) => {
    setFilters(newFilters);
  };

  const navigate = useNavigate();

  const clearFilters = () => {
    setFilters({
      status: "all",
      billCycle: "all",
      billingMonth: "all",
      dateRange: {
        from: null,
        to: null,
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const steps = [
    "bill-cycle",
    "fetch-consumer",
    "meter-readings",
    "bill-generation",
    "billing-summary",
    "approve",
  ];

  const handleEdit = async (bill: any) => {
    const selectedStep = steps[bill?.step?.stepsCompleted];

    if (selectedStep) {
      if (selectedStep === "billing-summary" || selectedStep === "approve") {
        console.log("navigating", selectedStep);

        navigate(
          `/bx/create-bill-generation/${bill?.id}/${bill?.billDraftData?.billRunId}/draft#${selectedStep}`
        );
      } else {
        navigate(
          `/bx/create-bill-generation/${bill?.id}/draft#${selectedStep}`
        );
      }
    }
  };

  // Define columns for the generic table
  const columns: TableColumn[] = [
    {
      key: "billDraftData",
      header: "Bill Cycle",
      type: "text",
      render: (value: any) => value?.billScheduleData?.billCycleName ?? "NA",
    },
    {
      key: "billDraftData",
      header: "Consumer Count",
      type: "text",
      render: (value: any) => value?.billScheduleData?.consumerCount ?? "NA",
    },
    {
      key: "billDraftData",
      header: "Utility Service",
      type: "text",
      render: (value: any) =>
        value?.billScheduleData?.utilityServices?.join(",") ?? "NA",
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) =>
          getStatusBadgeConfig(value as SingleBill["status"]).variant,
        getClassName: (value: string) =>
          getStatusBadgeConfig(value as SingleBill["status"]).className,
      },
    },
    {
      key: "step",
      header: "Step Working On",
      type: "text",
      render: (value: any) => {
        const step = value?.stepsCompleted;
        return typeof step === "number" ? step + 1 : "NA";
      },
    },
  ];

  // Define actions for the generic table
  const actions: TableAction[] = [
    {
      label: "View",
      onClick: (bill) => {
        handleEdit(bill);
      },
      icon: "Pen",
      variant: "outline",
    },
  ];

  return (
    <div className="space-y-4">
      <GenericTable
        data={draftDraftBills?.result ?? []}
        columns={columns}
        loading={false}
        rowKey="id"
        pagination={{
          currentPage: page,
          totalPages: Math.ceil((draftDraftBills?.count || 0) / PAGE_SIZE),
          totalItems: draftDraftBills?.count || 0,
          itemsPerPage: PAGE_SIZE,
          startItem: (page - 1) * PAGE_SIZE + 1,
          endItem: Math.min(page * PAGE_SIZE, draftDraftBills?.count || 0),
        }}
        onPageChange={handlePageChange}
        sortConfig={
          sortField && sortDirection
            ? { field: sortField, direction: sortDirection }
            : undefined
        }
        onSort={handleSort}
        actions={actions}
        actionsType="icons"
        emptyMessage="No bills found"
      />
    </div>
  );
};
